import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointAccountTypeData,
  type EnrollmentJointAccountTypeSubmitInput,
  type EnrollmentJointAccountTypeSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint AccountType data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointAccountTypeData>
 * @example const response = await getEnrollmentJointAccountTypeData()
 */
export const getEnrollmentJointAccountTypeData = async (
  context?: DefaultContext,
): Promise<EnrollmentJointAccountTypeData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointAccountTypeData',
      query: `mutation getEnrollmentJointAccountTypeData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          eligibleAccountTypes
          activePerson {
            path
            progress
            maritalStatus
            addressRegionCode
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointAccountTypeData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointAccountTypeData = createQueryHook<undefined, EnrollmentJointAccountTypeData>(
  getEnrollmentJointAccountTypeData,
)

/**
 * Submit Enrollment Joint AccountType data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointAccountTypeSubmitOutput>
 * @example const response = await submitJointEnrollmentJointAccountTypeData();
 */
export const submitEnrollmentJointAccountTypeData = async (
  enrollmentAccountTypeSubmitInput: EnrollmentJointAccountTypeSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointAccountTypeSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitJointEnrollmentJointAccountTypeData',
      query: `mutation submitJointEnrollmentJointAccountTypeData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
            applicationId
          }
        }
      }`,
    },
    {
      input: enrollmentAccountTypeSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointAccountTypeSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointAccountTypeData = createMutationHook<
  EnrollmentJointAccountTypeSubmitInput,
  EnrollmentJointAccountTypeSubmitOutput
>(submitEnrollmentJointAccountTypeData)
